'use client';

import { useEffect, useMemo, useState } from 'react';

import { ApiService } from '@/lib/api';
import AppHeroVideo from '../../ui/AppHeroVideo';
import AppImage from '../../ui/AppImage';
import { AppointmentBookingTrigger } from '../../appointments/AppointmentBookingTrigger';
import {
  CONSULTATION_SERVICE_SLUG,
} from '@/lib/constants/booking';
import { GET_SERVICE_CATEGORIES } from '@/lib/graphql';
import Icon from '../../ui/AppIcon';
import Link from 'next/link';
import { ServiceCategory } from '@/lib/types/service.types';
import { motion } from 'framer-motion';
import { useQuery } from '@apollo/client/react';

const CONSULTATION_FALLBACK_IMAGE = '/resource/full_banner.jpg';

export default function HeroSection() {
  const [consultationImage, setConsultationImage] = useState(CONSULTATION_FALLBACK_IMAGE);

  useEffect(() => {
    let cancelled = false;

    async function loadConsultationImage() {
      try {
        const service = await ApiService.getServiceBySlug(CONSULTATION_SERVICE_SLUG);
        const imageUrl = service?.image?.url?.trim();
        if (!cancelled && imageUrl) {
          setConsultationImage(imageUrl);
        }
      } catch {
        // Keep Unsplash fallback when the service or image is unavailable.
      }
    }

    void loadConsultationImage();
    return () => {
      cancelled = true;
    };
  }, []);

  const {
    data: serviceCategoriesData,
    loading: serviceCategoriesLoading,
    error: serviceCategoriesError,
  } = useQuery<{ serviceCategories: { data: ServiceCategory[] } }>(GET_SERVICE_CATEGORIES, {
    variables: {
      pagination: { page: 1, limit: 100 },
      filters: {
        status: 1,
      },
    },
  });

  const serviceCategories = useMemo<ServiceCategory[]>(() => {
    if (serviceCategoriesError) {
      console.error('GraphQL error details:', serviceCategoriesError);
      return [];
    }

    if (!serviceCategoriesLoading && serviceCategoriesData?.serviceCategories?.data) {
      const serviceCategoryList = serviceCategoriesData.serviceCategories.data;
      console.log('GET_SERVICE_CATEGORIES response:', serviceCategoriesData?.serviceCategories);
      return serviceCategoryList;
    }

    return [];
  }, [serviceCategoriesData, serviceCategoriesError, serviceCategoriesLoading]);

  return (
    <section className="relative min-h-[85vh] md:min-h-[90vh] flex items-center overflow-hidden">
      {/* Background - Image on Mobile, Video on Desktop */}
      <div className="absolute inset-0">
        {/* Image for Mobile */}
        <div className="block md:hidden w-full h-full">
          <AppImage
            src="/resource/full_banner.jpg"
            alt="Hair styling hero image"
            className="w-full h-full object-cover"
            priority
          />
        </div>

        {/* Video for Desktop */}
        <div className="hidden md:block w-full h-full">
          <AppHeroVideo
            src="/resource/video.mp4"
            alt="Hair styling hero video"
            className="w-full h-full object-cover"
            poster="/resource/full_banner.jpg"
            priority
          />
        </div>

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-transparent" />
      </div>
      {/* Content */}
      <div className="container mx-auto px-6 relative z-10 mt-14 lg:mt-0">
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="space-y-6">

            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-4 py-2">

              <Icon name="SparklesIcon" size={16} className="text-accent" />
              <span className="text-sm font-medium text-white">Your natural hair is enough</span>
            </motion.div>

            {/* Headline */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight">
              Quality hair care
              <br />
              <span className="text-accent">beautiful</span> hairstyles
            </h1>

            {/* Description */}
            <p className="text-lg md:text-xl text-white/90 max-w-2xl leading-relaxed">
              MEVOLY Hair &amp; Care is committed to providing you and your family with professional, gentle, high-quality hair care services.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap items-center gap-4 pt-4">

              <AppointmentBookingTrigger>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-4 cursor-pointer rounded-xl font-semibold text-lg shadow-lg shadow-primary/30 transition-all flex items-center gap-2">
                  Book Appointment
                  <Icon name="ArrowRightIcon" size={20} />
                </motion.button>
              </AppointmentBookingTrigger>

              {/* <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all flex items-center gap-2">

                <Icon name="PlayIcon" size={20} variant="solid" />
                Watch Video
              </motion.button> */}
            </div>
          </motion.div>

          {/* Floating Card */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="absolute bottom-8 right-8 hidden lg:block">

            <AppointmentBookingTrigger serviceSlugs={[CONSULTATION_SERVICE_SLUG]}>
              <button
                type="button"
                className="group block rounded-2xl transition-transform duration-300 hover:-translate-y-1 text-left cursor-pointer"
              >
                <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-xs transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0">
                      <AppImage
                        src={consultationImage}
                        alt="Quick consultation for new clients"
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1 group-hover:text-primary transition-colors">
                        For New Clients
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Start with a quick consultation to get to know your hair and your needs.
                      </p>
                    </div>
                  </div>
                </div>
              </button>
            </AppointmentBookingTrigger>          </motion.div>
        </div>
      </div>
      {/* Decorative Elements */}
      <motion.div
        animate={{
          y: [0, -20, 0]
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        className="absolute top-1/4 right-1/4 w-32 h-32 bg-accent/20 rounded-full blur-3xl" />
      <motion.div
        animate={{
          y: [0, 20, 0]
        }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1
        }}
        className="absolute bottom-1/4 left-1/4 w-40 h-40 bg-primary/20 rounded-full blur-3xl" />
      <div className='absolute w-full bottom-0 left-0 right-0 p-4 py-12 overflow-x-auto scroll-smooth scrollbar-hide'>
        <div className='flex flex-nowrap gap-8 justify-center items-center min-w-max px-4'>
          {serviceCategories.map((serviceCategory) => (
            <Link
              key={serviceCategory.id}
              href={`/services?category=${serviceCategory.slug}`}
              className='flex-shrink-0 group'
            >
              <h3 className='text-white text-xs font-bold whitespace-nowrap hover:text-accent transition-colors duration-200'>
                {serviceCategory.name}
              </h3>
            </Link>
          ))}
          <Link
            href='/services'
            className='flex-shrink-0 group'
          >
            <h3 className='text-white text-xs font-bold whitespace-nowrap hover:text-accent transition-colors duration-200'>
              More...
            </h3>
          </Link>
        </div>
      </div>
    </section>);



}